/**
 * 设计目标：
 * 1 流程细粒度拆解
 * 2 减少数据结构对上传逻辑影响
 */
var COS = require('cos-js-sdk-v5');
window.COS = COS;
var dataService = require('./dataService');
var logger = require('../logger');
var tool = require('../tool');
var noop = tool.noop;
var isFunction = tool.isFunction;
var util = require('./utils');
var cacheHelper = require('./cache-helper');

// 存储当前上传任务信息
var currentUploadTasks = {};

/**
 * 清理指定的上传任务信息
 * @param {string} uploadId - 上传ID
 */
function cleanupTask(uploadId) {
    if (uploadId && currentUploadTasks[uploadId]) {
        logger.log('[COS Multipart] cleanupTask', uploadId);
        delete currentUploadTasks[uploadId];
    }
}

/**
 * 清理上传缓存（如果启用了缓存）
 * @param {Object} uploadOptions - 上传配置对象
 */
function clearUploadCache(uploadOptions) {
    if (uploadOptions && uploadOptions.supportsResumeUpload && uploadOptions._fileMD5 && uploadOptions._teacherId) {
        cacheHelper.clearBucketInfoCache(uploadOptions._teacherId, uploadOptions._fileMD5).catch(function (err) {
            logger.errorAndLog('[COS Multipart] Cache cleanup error:', err);
        });
    }
}

/**
 * 清理上传任务（包括缓存和任务信息）
 * @param {string} uploadId - 上传ID
 * @param {Object} uploadOptions - 上传配置对象
 */
function cleanupUploadTask(uploadId, uploadOptions) {
    clearUploadCache(uploadOptions);
    cleanupTask(uploadId);
}

/**
 * 获取media type，主要为了隔离具体的数据结构和上传逻辑
 * @param {*} uploadOptions
 * @returns
 */
function getMediaType(uploadOptions) {
    if (!uploadOptions || !uploadOptions.signature) {
        logger.log('[COS Multipart] getMediaType uploadOptions invalid', uploadOptions);
        return '';
    }
    return uploadOptions.signature.media_type;
}

/**
 * 获取id_prefix，主要为了隔离具体的数据结构和上传逻辑
 * @returns
 */
function getIdPrefix(uploadOptions) {
    if (!uploadOptions || !uploadOptions.signature) {
        return '';
    }
    return uploadOptions.signature.id_prefix || 'teacher';
}

/**
 * 获取存储信息和存储授权信息
 * @param {*} uploadOptions
 * @returns
 */
function getBucketInfoAndCredentialInfo(file, uploadOptions) {
    return new Promise(function (resolve, reject) {
        if (!uploadOptions || !uploadOptions.signature) {
            logger.errorAndLog('[COS Multipart] getBucketInfoAndCredentialInfo uploadOptions invalid:', uploadOptions);
            reject('uploadOptions invalid');
            return;
        }
        var mediaType = getMediaType(uploadOptions);
        var idPrefix = getIdPrefix(uploadOptions);
        var ext = tool.getFileExt(file.name);
        var params = {
            media_type: mediaType,
            id_prefix: idPrefix,
            id: uploadOptions.signature.id,
            ext: ext,
        };
        dataService
            .getBucketInfoAndCredentialInfo(params)
            .then(function (result) {
                resolve(result);
            })
            .catch(function (err) {
                logger.errorAndLog('[COS Multipart] getBucketInfoAndCredentialInfo error:', err);
                reject(err);
            });
    });
}

/**
 * 获取对象上传凭证
 * @param {string} resource_id - 从 GenObjPathWithCredential 返回的 resource_id
 * @param {string} cred_type - 固定使用 token
 * @returns {Promise} {credential_info}
 * 使用场景：凭证过期续期、断点续传
 */
function getObjectToken(resource_id, cred_type = 'token') {
    return new Promise(function (resolve, reject) {
        if (!resource_id) {
            logger.errorAndLog('[COS Multipart] getObjectToken resource_id invalid', resource_id);
            reject(new Error('[COS Multipart] getObjectToken resource_id invalid'));
            return;
        }
        dataService
            .getObjectToken({ resource_id: resource_id, cred_type: cred_type })
            .then(function (result) {
                resolve(result);
            })
            .catch(function (err) {
                logger.errorAndLog('[COS Multipart] getObjectToken error:', err);
                reject(err);
            });
    });
}

/**
 * 取消上传任务
 * @param {string} uploadId - 上传ID，由upload方法返回
 * @returns {boolean} 是否成功取消任务
 */
function cancelUpload(uploadId) {
    if (!uploadId) {
        logger.errorAndLog('[COS Multipart] cancelUpload uploadId invalid', uploadId);
        return false;
    }

    var taskInfo = currentUploadTasks[uploadId];
    if (!taskInfo) {
        logger.errorAndLog('[COS Multipart] cancelUpload task not found', uploadId);
        return false;
    }

    try {
        // 使用COS SDK的取消任务功能
        if (taskInfo.cos && taskInfo.taskId) {
            taskInfo.cos.cancelTask(taskInfo.taskId);
            logger.log('[COS Multipart] cancelUpload task canceled', uploadId, taskInfo.taskId);
        }

        // 如果是分块上传，还需要中止分块上传任务
        if (taskInfo.cos && taskInfo.bucketInfo && taskInfo.objectName && taskInfo.uploadId) {
            taskInfo.cos.abortUploadTask(
                {
                    Bucket: taskInfo.bucketInfo.name,
                    Region: taskInfo.bucketInfo.region,
                    Key: taskInfo.objectName,
                    UploadId: taskInfo.uploadId,
                    Level: 'task',
                },
                function (err, data) {
                    if (err) {
                        logger.errorAndLog('[COS Multipart] abortUploadTask error', err);
                    } else {
                        logger.log('[COS Multipart] abortUploadTask success', data);
                    }
                }
            );
        }

        // 清理任务信息
        cleanupTask(uploadId);

        // 触发取消回调
        if (taskInfo.cancelCallback && isFunction(taskInfo.cancelCallback)) {
            taskInfo.cancelCallback();
        }

        return true;
    } catch (error) {
        logger.errorAndLog('[COS Multipart] cancelUpload error', error);
        return false;
    }
}

/**
 * 具体的上传处理
 * @param {*} file
 * @param {*} uploadOptions
 * @param {*} bucketInfoAndCredentialInfo
 * @param {string} uploadId - 上传ID
 * @returns
 */
function uploadProcess(file, uploadOptions, bucketInfoAndCredentialInfo, uploadId) {
    if (!bucketInfoAndCredentialInfo) {
        logger.errorAndLog('[COS Multipart] bucketInfoAndCredentialInfo is null');
        uploadOptions.error && uploadOptions.error();
        return;
    }
    var mediaType = getMediaType(uploadOptions);
    // var idPrefix = getIdPrefix(uploadOptions);
    var ext = tool.getFileExt(file.name);
    var data = bucketInfoAndCredentialInfo || {};
    var credentials = data.credential_info;
    var onError = function (errorMessage) {
        logger.errorAndLog(errorMessage);
        // 清理任务信息
        cleanupTask(uploadId);
        uploadOptions.error && uploadOptions.error();
    };

    if (!credentials) {
        onError('[COS Multipart] uploadProcess credentials invalid');
        return;
    }

    var resourceId = data.resource_id || '';
    if (!resourceId) {
        onError('[COS Multipart] uploadProcess resource_id invalid');
        return;
    }

    var objectName = data.object_name || '';
    if (!objectName) {
        onError('[COS Multipart] uploadProcess object_name invalid');
        return;
    }

    var bucketInfo = data.bucket_info;
    if (!bucketInfo || !bucketInfo.name || !bucketInfo.region) {
        logger.errorAndLog('[COS Multipart] uploadProcess bucket_info invalid', bucketInfo);
        // 清理任务信息
        cleanupTask(uploadId);
        uploadOptions.error && uploadOptions.error();
        return;
    }

    // var domain = 'umu-test.umucdn.cn';
    var domain = (bucketInfo.name || '').replace(/-\d*$/i, '') + '.umucdn.cn';
    var protocol = util.getProtocol(domain);
    var cos = new COS({
        Protocol: protocol,
        Domain: domain,
        getAuthorization: function (options, callback) {
            callback({
                TmpSecretId: credentials.tmp_ak,
                TmpSecretKey: credentials.tmp_sk,
                SecurityToken: credentials.session_token,
                // 建议返回服务器时间作为签名的开始时间，避免用户浏览器本地时间偏差过大导致签名错误
                StartTime: credentials.start_time, // 时间戳，单位秒，如：1580000000
                ExpiredTime: credentials.expire_time, // 时间戳，单位秒，如：1580000900
            });
        },
    });

    // 更新任务信息，存储cos实例和相关信息以便取消
    if (currentUploadTasks[uploadId]) {
        currentUploadTasks[uploadId].cos = cos;
        currentUploadTasks[uploadId].bucketInfo = bucketInfo;
        currentUploadTasks[uploadId].objectName = objectName;
        currentUploadTasks[uploadId].cancelCallback = uploadOptions.onCancel;
    }

    cos.uploadFile(
        {
            Bucket: bucketInfo.name /* 必须 */,
            Region: bucketInfo.region /* 存储桶所在地域，必须字段 */,
            Key: objectName /* 必须 */,
            Body: file /* 必须 */,
            SliceSize:
                1024 *
                1024 *
                (parseInt(uploadOptions.sliceSize || '5') || 5) /* 触发分块上传的阈值，超过5MB使用分块上传，非必须 */,
            onTaskReady: function (taskId) {
                /* 非必须 */
                logger.log('[COS Multipart] onTaskReady', taskId);
                // 存储taskId以便取消任务
                if (currentUploadTasks[uploadId]) {
                    currentUploadTasks[uploadId].taskId = taskId;
                }
                uploadOptions.onTaskReady && uploadOptions.onTaskReady(taskId);
            },
            onProgress: function (progressData) {
                /* 非必须 */
                // var percent = Math.floor(progressData.percent * 10000) / 100;
                //var speed = Math.floor((progressData.speed / 1024 / 1024) * 100) / 100;
                // logger.log('[COS Multipart] onProgress', percent, speed);
                // 检查任务是否被取消
                if (!currentUploadTasks[uploadId]) {
                    logger.log('[COS Multipart] task canceled, stop progress callback');
                    return;
                }
                // 更新上传速度
                uploadOptions.progress(progressData);
            },
            onFileFinish: function (err, data) {
                var success = function (source) {
                    // eslint-disable-next-line max-len
                    var sourcePath = /https?:\/\//i.test(source.Location)
                        ? source.Location
                        : protocol + '//' + source.Location;

                    var resourceInfo = {
                        resource_id: resourceId,
                        path: sourcePath,
                    };

                    if (!uploadOptions.customCallback) {
                        dataService
                            .callbackOnObjectUploaded({
                                media_type: mediaType,
                                resource_id: resourceId,
                                file_name: file.name,
                                file_size: file.size,
                                path: sourcePath,
                                ext: ext,
                                extend_info: uploadOptions.extend_info || '',
                            })
                            .then(
                                function () {
                                    // 清理任务信息和缓存
                                    cleanupUploadTask(uploadId, uploadOptions);
                                    uploadOptions.success(null, resourceInfo);
                                },
                                function (error) {
                                    logger.errorAndLog('[COS Multipart] callbackOnObjectUploaded-1 error', error);
                                    // 清理任务信息和缓存
                                    cleanupUploadTask(uploadId, uploadOptions);
                                    error(error);
                                }
                            )
                            .catch(function (e) {
                                logger.errorAndLog('[COS Multipart] callbackOnObjectUploaded-2 error', e);
                                // 清理任务信息和缓存
                                cleanupUploadTask(uploadId, uploadOptions);
                                error(e);
                            });
                    } else {
                        if (isFunction(uploadOptions.customCallback)) {
                            var callbackPromise = Promise.resolve(uploadOptions.customCallback(resourceInfo));
                            // 默认10s超时
                            var timeoutPromise = new Promise(function (_, reject) {
                                setTimeout(function () {
                                    reject(new Error('[COS Multipart] custom callback timeout'));
                                }, 10000);
                            });
                            Promise.race([callbackPromise, timeoutPromise])
                                .catch(function (e) {
                                    logger.errorAndLog('[COS Multipart] custom callback error', e);
                                })
                                .finally(function () {
                                    // 清理任务信息和缓存
                                    cleanupUploadTask(uploadId, uploadOptions);
                                });
                        } else {
                            // 清理任务信息和缓存
                            cleanupUploadTask(uploadId, uploadOptions);
                        }
                    }
                };

                var error = function (err) {
                    var errorMessage = (err && err.message) || 'unknown error';
                    logger.errorAndLog('[COS Multipart] upload fail callback error', errorMessage);
                    // 清理任务信息
                    cleanupTask(uploadId);
                    if (typeof uploadOptions.error === 'function') {
                        uploadOptions.error(errorMessage);
                    } else {
                        logger.errorAndLog('[COS Multipart] error callback not defined');
                    }
                };

                // 检查任务是否被取消
                if (!currentUploadTasks[uploadId]) {
                    logger.log('[COS Multipart] task canceled, stop finish callback');
                    return;
                }

                if (err) {
                    error(err);
                } else {
                    success(data);
                    logger.log('[COS Multipart] upload success:', objectName);
                }
            },
        },
        function (err, data) {
            // 记录最终的网络状态
            logger.log('[COS Multipart] uploadProcess done', err, data);
            // 检查是否是网络错误
            if (
                err &&
                ((err.code && ['NetworkingError', 'NETWORK_ERROR'].includes(err.code)) ||
                    (err.message && err.message.indexOf('network error') > -1))
            ) {
                uploadOptions.error && uploadOptions.error(err);
                return;
            }
        }
    );
}

function processBucketInfoAndCredentialInfo(file, uploadOptions, bucketInfoAndCredentialInfo, uploadId) {
    if (bucketInfoAndCredentialInfo) {
        uploadProcess(file, uploadOptions, bucketInfoAndCredentialInfo, uploadId);
    } else {
        getBucketInfoAndCredentialInfo(file, uploadOptions).then(
            function (result) {
                if (!result || result.error_code != 0 || !result.data) {
                    logger.log('[COS Multipart] getBucketInfoAndCredentialInfo result invalid', result);
                    cleanupTask(uploadId);
                    uploadOptions.error && uploadOptions.error();
                    return;
                }
                var data = result.data || {};
                uploadProcess(file, uploadOptions, data, uploadId);
            },
            function (err, data) {
                logger.errorAndLog('[COS Multipart] getBucketInfoAndCredentialInfo error', err, data);
                cleanupTask(uploadId);
                uploadOptions.error && uploadOptions.error();
            }
        );
    }
}

async function resaveBucketInfoToCache(fileMD5, uploadOptions, bucketInfoAndCredentialInfo) {
    const clearResult = await cacheHelper.clearBucketInfoCache(uploadOptions.signature.id, fileMD5);
    const saveResult = await cacheHelper.saveBucketInfoToCache(
        uploadOptions.signature.id,
        fileMD5,
        bucketInfoAndCredentialInfo
    );
    logger.log('[COS Multipart] resaveBucketInfoToCache clearResult:', clearResult);
    logger.log('[COS Multipart] resaveBucketInfoToCache saveResult:', saveResult);
}

async function tryResumeUpload(file, uploadOptions, bucketInfoAndCredentialInfo, uploadId) {
    if (!uploadOptions.supportsResumeUpload) {
        processBucketInfoAndCredentialInfo(file, uploadOptions, bucketInfoAndCredentialInfo, uploadId);
        return;
    }
    try {
        const fileMD5 = await cacheHelper.calculateFileMD5(file);
        if (!fileMD5) {
            logger.log('[COS Multipart] tryResumeUpload fileMD5 invalid');
            processBucketInfoAndCredentialInfo(file, uploadOptions, bucketInfoAndCredentialInfo, uploadId);
            return;
        }
        uploadOptions._fileMD5 = fileMD5;
        uploadOptions._teacherId = uploadOptions.signature.id;
        const cachedInfo = await cacheHelper.findCachedBucketInfo(uploadOptions.signature.id, fileMD5);
        if (!cachedInfo || !cachedInfo.resource_id || !cachedInfo.object_name || !cachedInfo.bucket_info) {
            logger.log('[COS Multipart] tryResumeUpload cachedInfo invalid');
            await resaveBucketInfoToCache(fileMD5, uploadOptions, bucketInfoAndCredentialInfo);
            processBucketInfoAndCredentialInfo(file, uploadOptions, bucketInfoAndCredentialInfo, uploadId);
            return;
        }
        const result = await getObjectToken(cachedInfo.resource_id);
        if (!result || result.error_code != 0 || !result.data || !result.data.credential_info) {
            logger.log('[COS Multipart] tryResumeUpload getObjectToken result invalid');
            await resaveBucketInfoToCache(fileMD5, uploadOptions, bucketInfoAndCredentialInfo);
            processBucketInfoAndCredentialInfo(file, uploadOptions, bucketInfoAndCredentialInfo, uploadId);
            return;
        }
        const credentialInfo = result.data.credential_info;
        if (
            !credentialInfo ||
            !credentialInfo.tmp_ak ||
            !credentialInfo.tmp_sk ||
            !credentialInfo.session_token ||
            !credentialInfo.start_time ||
            !credentialInfo.expire_time
        ) {
            logger.log('[COS Multipart] tryResumeUpload credentialInfo invalid', credentialInfo);
            await resaveBucketInfoToCache(fileMD5, uploadOptions, bucketInfoAndCredentialInfo);
            return;
        }
        cachedInfo.credential_info = credentialInfo;
        processBucketInfoAndCredentialInfo(file, uploadOptions, cachedInfo, uploadId);
    } catch (error) {
        logger.errorAndLog('[COS Multipart] tryResumeUpload error:', error);
        processBucketInfoAndCredentialInfo(file, uploadOptions, bucketInfoAndCredentialInfo, uploadId);
    }
}

function upload(file, uploadOptions, bucketInfoAndCredentialInfo) {
    if (!file) {
        logger.errorAndLog('[COS Multipart] file invalid:', file);
        uploadOptions.error && uploadOptions.error();
        return;
    }

    logger.log('[COS Multipart] start');
    if (!uploadOptions || !uploadOptions.signature.media_type) {
        logger.errorAndLog('[COS Multipart] uploadOptions invalid:', uploadOptions);
        uploadOptions.error && uploadOptions.error();
        return;
    }

    // 生成唯一的上传ID
    var uploadId = file.uniqueId || 'upload_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11);

    // 初始化任务信息
    currentUploadTasks[uploadId] = {
        file: file,
        uploadOptions: uploadOptions,
        startTime: Date.now(),
    };

    // var mediaType = uploadOptions.signature.media_type;
    // var idPrefix = uploadOptions.signature.id_prefix || 'teacher';
    uploadOptions = Object.assign(
        {
            beforeUpload: noop,
            success: noop,
            error: noop,
            progress: noop,
        },
        uploadOptions
    );

    //beforeUpload 之前文件校验
    if (tool.isOverMaxSize(file, uploadOptions.maxFileSize, uploadOptions.onOverMaxSize)) {
        logger.errorAndLog('[COS Multipart] isOverMaxSize');
        cleanupTask(uploadId);
        uploadOptions.error && uploadOptions.error();
        return;
    }
    //beforeUpload
    if (!uploadOptions.beforeUpload.apply(null, [file])) {
        logger.errorAndLog('[COS Multipart] uploadOptions invalid', uploadOptions);
        cleanupTask(uploadId);
        uploadOptions.error && uploadOptions.error();
        return;
    }

    // 断点续传逻辑
    if (uploadOptions.supportsResumeUpload) {
        tryResumeUpload(file, uploadOptions, bucketInfoAndCredentialInfo, uploadId);
        return;
    }
    // 原有的非断点续传逻辑
    processBucketInfoAndCredentialInfo(file, uploadOptions, bucketInfoAndCredentialInfo, uploadId);

    // 返回上传ID，用于取消上传
    return uploadId;
}

/**
 * 获取当前所有上传任务的状态
 * @returns {Array} 当前上传任务列表
 */
function getUploadTasks() {
    var tasks = [];
    for (var uploadId in currentUploadTasks) {
        if (currentUploadTasks.hasOwnProperty(uploadId)) {
            var task = currentUploadTasks[uploadId];
            tasks.push({
                uploadId: uploadId,
                fileName: task.file ? task.file.name : '',
                fileSize: task.file ? task.file.size : 0,
                startTime: task.startTime,
                taskId: task.taskId,
            });
        }
    }
    return tasks;
}

module.exports = {
    upload: upload,
    getBucketInfoAndCredentialInfo: getBucketInfoAndCredentialInfo,
    cancelUpload: cancelUpload,
    getUploadTasks: getUploadTasks,
};
